import type { BookVoiceAssignmentsRow, ManualVoiceAssignment } from "@/lib/castAnalysisTypes";
import { DEFAULT_VOICE_SETTINGS } from "@/lib/mockElevenVoices";

export type CharacterVoiceAssignmentApiItem = {
  story_id: string;
  character_id: string;
  voice_id: string;
  notes: string | null;
  updated_at: string | null;
  provider_name: string;
  preview_url: string;
  gender: string;
  accent: string;
  labels: Record<string, unknown>;
};

/**
 * Merge server Postgres assignments into a local IndexedDB row.
 * If both sides have timestamps, keeps the local assignment when local `client_updated_at` is newer than server `updated_at`.
 */
export function mergeServerCharacterVoices(
  base: BookVoiceAssignmentsRow,
  items: CharacterVoiceAssignmentApiItem[],
  narratorCharacterId: string | null,
): BookVoiceAssignmentsRow {
  let narrator = base.narrator;
  const characters = { ...base.characters };

  for (const item of items) {
    const serverTs = item.updated_at ? Date.parse(item.updated_at) : NaN;
    const slotNarrator = Boolean(
      (base.narrator && item.character_id === base.narrator.character) ||
        (narratorCharacterId !== null && narratorCharacterId !== "" && item.character_id === narratorCharacterId),
    );

    const localSlot: ManualVoiceAssignment | null | undefined = slotNarrator ? narrator : characters[item.character_id];
    const localTs = localSlot?.client_updated_at ? Date.parse(localSlot.client_updated_at) : NaN;

    if (localSlot?.client_updated_at && item.updated_at) {
      if (!Number.isNaN(localTs) && !Number.isNaN(serverTs) && localTs > serverTs) {
        continue;
      }
    }

    const label =
      typeof item.provider_name === "string" && item.provider_name.trim() ? item.provider_name.trim() : item.voice_id;
    const next: ManualVoiceAssignment = {
      character: item.character_id,
      voice_id: item.voice_id,
      voice_name: label,
      voice_settings: { ...DEFAULT_VOICE_SETTINGS },
      client_updated_at: item.updated_at ?? new Date().toISOString(),
    };

    if (slotNarrator) narrator = next;
    else characters[item.character_id] = next;
  }

  return { ...base, narrator, characters };
}
